import { ButtonLink } from '@lifeomic/chroma-react/components/ButtonLink';
import {
  Archive,
  CloudOff,
  Link,
  LinkBroken,
  Lock,
} from '@lifeomic/chromicons';
import { CSVView } from './CSVView';
import { defineMessages, useIntl } from 'react-intl';
import { DocumentContent } from './DocumentContent';
import { ImageBox } from './ImageBox';
import { LoadingContainer } from './LoadingContainer';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { Notebook } from './Notebook';
import { RawText } from './RawText';
import { VIEWABLE_SIZE_LIMIT_5MB, canRender } from '../util';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import FileService, {
  useSubjectRestrictedOptions,
} from '@lifeomic/phc-web-toolkit/src/services/FileService';
import React from 'react';
import urls from '../../../../util/urls';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService/useService';
import { useCustomCompareEffect } from 'react-use';

const fileService = new FileService();

const useStyles = makeStyles(theme => ({
  root: {},
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  fileNotSupportedContainer: {
    display: 'flex',
    height: '100%',
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  downloadButtonLink: {
    marginTop: theme.spacing(2),
  },
}));

const messages = defineMessages({
  fileNotSupported: {
    id: 'files.fileDetailsModal.fileNotSupported',
    defaultMessage:
      'It looks like the file size is too large or is an unsupported type to view within PHC.  Consider downloading or opening it in your browser directly by clicking the link below!',
  },
  cannotAccessFile: {
    id: 'files.fileDetailsModal.cannotAccessFile',
    defaultMessage:
      'You do not have access to view the contents of this file.  Contact your project administrator if you need access.',
  },
  downloadButtonLink: {
    id: 'files.fileDetailsModal.downloadButtonLink',
    defaultMessage: 'Open file',
  },
  openInDicomViewerDesc: {
    id: 'files.fileDetailsModal.openInDicomViewerDesc',
    defaultMessage:
      'This file cannot be viewed here, as it is too large; however, it can be viewed in DICOM Viewer directly.',
  },
  openInDicomViewer: {
    id: 'files.fileDetailsModal.openInDicomViewer',
    defaultMessage: 'Open in DICOM Viewer',
  },
  failedToLoadFileOther: {
    id: 'files.fileDetailsModal.failedToLoadFileOther',
    defaultMessage: 'Failed to load file',
  },
  failedToLoadFileArchive: {
    id: 'files.fileDetailsModal.failedToLoadFileArchive',
    defaultMessage:
      'This file is not available to view because it is currently archived. Contact LifeOmic support to learn more.',
  },
});

export const FileDetailsModal = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  file,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  hasViewAccess,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  isOpen,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  onDismiss,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const [fetchOpenLink, fetchOpenLinkState] = useService(
    fileService.fetchOpenLink,
  );
  const [showCode, setShowCode] = React.useState(false);

  const [downloadedFile, setDownloadedFile] = React.useState(null);
  const [isFileSupported, setIsFileSupported] = React.useState(false);
  const [failedToLoad, setFailedToLoad] = React.useState<
    'archived' | 'other' | false
  >(false);
  const cohortRestrictedOptions = useSubjectRestrictedOptions();

  React.useEffect(() => {
    if (!file) {
      return;
    }

    if (file.size && file.size.raw > VIEWABLE_SIZE_LIMIT_5MB) {
      setIsFileSupported(false);
    } else if (hasViewAccess && file.contentType && canRender(file)) {
      setIsFileSupported(true);
    }

    if (hasViewAccess) {
      fetchOpenLink(file.id, cohortRestrictedOptions);
    }
  }, [
    file,
    fetchOpenLink,
    hasViewAccess,
    cohortRestrictedOptions,
    formatMessage,
  ]);

  useCustomCompareEffect(
    () => {
      if (fetchOpenLinkState.response && fetchOpenLinkState.response.data) {
        if (
          fetchOpenLinkState.response.data?.contentType === 'application/pdf'
        ) {
          // for pdfs, leverage the browser's native viewer
          window.open(fetchOpenLinkState.response.data.downloadUrl, '_blank');
          onDismiss();
        } else {
          setDownloadedFile(fetchOpenLinkState.response.data);
        }
      } else if (fetchOpenLinkState.error) {
        const isArchived = fetchOpenLinkState.error.response?.status === 422;
        setFailedToLoad(isArchived ? 'archived' : 'other');
      }
    },
    [fetchOpenLinkState.response, onDismiss],
    ([prevResponse], [curResponse]) => prevResponse === curResponse,
  );

  return (
    <Modal
      isOpen={isOpen}
      title={file.name}
      onDismiss={onDismiss}
      contentClassName={classes.root}
      isFullScreen
    >
      {fetchOpenLinkState.isFetching && !downloadedFile && <LoadingContainer />}
      {!hasViewAccess && (
        <div className={classes.fileNotSupportedContainer}>
          <EmptyPageState
            illustration={Lock}
            message={formatMessage(messages.cannotAccessFile)}
          />
        </div>
      )}
      {failedToLoad && (
        <div className={classes.fileNotSupportedContainer}>
          <EmptyPageState
            illustration={failedToLoad === 'archived' ? Archive : LinkBroken}
            message={formatMessage(
              failedToLoad === 'archived'
                ? messages.failedToLoadFileArchive
                : messages.failedToLoadFileOther,
            )}
          />
        </div>
      )}
      {/* Link out to DICOM Viewer app for dicom files */}
      {downloadedFile &&
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        downloadedFile.id &&
        hasViewAccess &&
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        (downloadedFile.contentType === 'application/dicom' ||
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          downloadedFile.name.endsWith('.dcm')) && (
          <div className={classes.fileNotSupportedContainer}>
            <EmptyPageState
              illustration={Link}
              message={formatMessage(messages.openInDicomViewerDesc)}
            />
            <ButtonLink
              className={classes.downloadButtonLink}
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              to={urls.app.dicomViewer(downloadedFile.id)}
              variant="text"
              target="_blank"
            >
              {formatMessage(messages.openInDicomViewer)}
            </ButtonLink>
          </div>
        )}
      {/* Not a dicom file, too big, or not supported */}
      {downloadedFile &&
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        downloadedFile.downloadUrl &&
        hasViewAccess &&
        !isFileSupported &&
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        (downloadedFile.contentType !== 'application/dicom' ||
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          !downloadedFile.name.endsWith('.dcm')) && (
          <div className={classes.fileNotSupportedContainer}>
            <EmptyPageState
              illustration={CloudOff}
              message={formatMessage(messages.fileNotSupported)}
            />
            <ButtonLink
              className={classes.downloadButtonLink}
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              to={downloadedFile.downloadUrl}
              variant="text"
            >
              {formatMessage(messages.downloadButtonLink)}
            </ButtonLink>
          </div>
        )}
      {downloadedFile && isFileSupported && (
        <DocumentContent
          file={downloadedFile}
          showCode={showCode}
          setShowCode={setShowCode}
        >
          {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
          {downloadedFile.contentType.startsWith('image') && (
            <ImageBox file={downloadedFile} />
          )}
          {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
          {(downloadedFile.contentType === 'text/csv' ||
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            downloadedFile.contentType === 'text/tab-separated-values') && (
            <CSVView file={downloadedFile} />
          )}
          {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
          {downloadedFile.contentType !== 'text/csv' &&
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            downloadedFile.contentType !== 'text/tab-separated-values' &&
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            (downloadedFile.contentType.startsWith('text') ||
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              downloadedFile.name.endsWith('.hl7') ||
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              downloadedFile.name.endsWith('.grl') ||
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              downloadedFile.contentType === 'application/json' ||
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              downloadedFile.contentType === 'application/xml' ||
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              downloadedFile.contentType === 'application/cwl' ||
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              downloadedFile.name.endsWith('.cwl')) && (
              <RawText file={downloadedFile} />
            )}
          {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
          {(downloadedFile.contentType === 'application/x-ipynb+json' ||
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            downloadedFile.name.endsWith('.ipynb')) && (
            <Notebook file={downloadedFile} showCode={showCode} />
          )}
        </DocumentContent>
      )}
    </Modal>
  );
};
